<template>
  <section class="update-dashboard" aria-label="CourtListener Updates">
    <header class="dashboard-header">
      <div>
        <p class="eyebrow">Via CourtListener</p>
        <h2>Litigation Updates</h2>
      </div>
    </header>

    <form
      v-if="showAddMonitor"
      class="monitor-form"
      @submit.prevent="saveMonitor"
    >
      <div class="form-heading">
        <div>
          <h3>Add Monitor</h3>
          <p>Start with the CourtListener docket page for the MDL master docket.</p>
        </div>

        <button
          type="button"
          class="close-button"
          :disabled="saving"
          aria-label="Close Add Monitor form"
          @click="closeAddMonitor"
        >
          <span class="material-icons-outlined" aria-hidden="true">close</span>
        </button>
      </div>

      <div class="field-grid">
        <label class="field-group full-width">
          <span>LLF Litigation</span>
          <select
            v-model="form.litigationId"
            required
            :disabled="loading || litigations.length === 0"
            @change="applyLitigationName"
          >
            <option value="" disabled>Select a Litigation</option>
            <option
              v-for="litigation in availableLitigations"
              :key="litigation.litigationId"
              :value="litigation.litigationId"
            >
              {{ litigationLabel(litigation) }}
            </option>
          </select>
          <small v-if="litigations.length > availableLitigations.length">
            Litigations that already have a monitor are hidden.
          </small>
        </label>

        <label class="field-group">
          <span>MDL Number</span>
          <input
            v-model="form.mdlNumber"
            type="number"
            min="1"
            step="1"
            inputmode="numeric"
            placeholder="For example, 3094"
            required
          />
        </label>

        <label class="field-group">
          <span>CourtListener Docket ID</span>
          <input
            v-model="form.courtListenerDocketId"
            type="number"
            min="1"
            step="1"
            inputmode="numeric"
            placeholder="Numeric ID from the docket URL"
            required
          />
          <small>This is the number after <code>/docket/</code> in CourtListener.</small>
        </label>

        <label class="field-group">
          <span>Court ID</span>
          <input
            v-model="form.courtId"
            type="text"
            maxlength="40"
            autocomplete="off"
            placeholder="For example, laed"
            required
          />
        </label>

        <label class="field-group">
          <span>Docket Number</span>
          <input
            v-model="form.docketNumber"
            type="text"
            maxlength="120"
            autocomplete="off"
            placeholder="For example, 2:14-md-02592"
            required
          />
        </label>

        <label class="field-group full-width">
          <span>Case Name</span>
          <input
            v-model="form.caseName"
            type="text"
            maxlength="300"
            autocomplete="off"
            required
          />
        </label>

        <label class="field-group full-width">
          <span>CourtListener Alert ID <em>Optional</em></span>
          <input
            v-model="form.courtListenerAlertId"
            type="number"
            min="1"
            step="1"
            inputmode="numeric"
            placeholder="Can be added later after the docket alert is created"
          />
        </label>
      </div>

      <p v-if="formError" class="message error-message" role="alert">
        {{ formError }}
      </p>

      <div class="form-actions">
        <button
          type="button"
          class="cancel-button"
          :disabled="saving"
          @click="closeAddMonitor"
        >
          Cancel
        </button>

        <button
          type="submit"
          class="save-button"
          :disabled="saving || !canSave"
        >
          {{ saving ? "Saving..." : "Save Monitor" }}
        </button>
      </div>
    </form>

    <p v-if="successMessage" class="message success-message" role="status">
      {{ successMessage }}
    </p>

    <div class="updates-tabs" role="tablist" aria-label="Litigation update views">
      <button
        id="incoming-updates-tab"
        type="button"
        role="tab"
        :class="{ active: activeTab === 'incoming' }"
        :aria-selected="activeTab === 'incoming'"
        aria-controls="incoming-updates-panel"
        @click="activeTab = 'incoming'"
      >
        Incoming Updates
        <span>{{ pendingReviewCount }}</span>
      </button>

      <button
        id="monitored-mdls-tab"
        type="button"
        role="tab"
        :class="{ active: activeTab === 'monitors' }"
        :aria-selected="activeTab === 'monitors'"
        aria-controls="monitored-mdls-panel"
        @click="activeTab = 'monitors'"
      >
        Monitored MDLs
        <span>{{ monitors.length }}</span>
      </button>
    </div>

    <section
      v-if="activeTab === 'incoming'"
      id="incoming-updates-panel"
      class="review-queue"
      role="tabpanel"
      aria-labelledby="incoming-updates-tab"
    >
      <div class="list-heading">
        <div>
          <h3 id="review-queue-title">Incoming Updates</h3>
          <p>
            {{ pendingReviewCount }}
            {{ pendingReviewCount === 1 ? "filing needs" : "filings need" }} review
          </p>
        </div>

        <div class="queue-actions">
          <span class="private-label">
            <span class="material-icons-outlined" aria-hidden="true">lock</span>
            Internal review only
          </span>

          <button
            type="button"
            class="refresh-button"
            :disabled="loading"
            @click="loadDashboard"
          >
            {{ loading ? "Loading..." : "Refresh" }}
          </button>
        </div>
      </div>

      <div v-if="loading" class="empty-state">Loading incoming filings...</div>

      <div v-else-if="loadError" class="empty-state error-state">
        {{ loadError }}
      </div>

      <div v-else-if="updateEvents.length === 0" class="empty-state">
        <span class="material-icons-outlined" aria-hidden="true">inbox</span>
        <strong>No CourtListener filings are waiting for review.</strong>
        <span>New filings from linked MDLs will appear here automatically.</span>
      </div>

      <div v-else class="event-list">
        <article
          v-for="updateEvent in updateEvents"
          :key="updateEvent.id"
          class="event-card"
        >
          <header class="event-header">
            <div>
              <div class="event-kicker">
                <span>MDL {{ updateEvent.mdlNumber }}</span>
                <span v-if="updateEvent.entryNumber">Entry {{ updateEvent.entryNumber }}</span>
                <span>{{ formatDate(updateEvent.dateFiled || updateEvent.receivedAt) }}</span>
              </div>

              <h4>{{ linkedLitigationName(updateEvent.litigationId) }}</h4>
              <p>{{ updateEvent.caseName }} · {{ updateEvent.docketNumber }}</p>
            </div>

            <div class="event-badges">
              <span
                class="triage-pill"
                :class="triagePriorityClass(updateEvent)"
              >
                {{ triageLabel(updateEvent) }}
              </span>
              <span class="review-pill">Needs Review</span>
            </div>
          </header>

          <p class="event-description">
            {{ updateEvent.description || "CourtListener did not provide a filing description." }}
          </p>

          <section
            v-if="updateEvent.aiTriage?.status === 'completed'"
            class="ai-triage-card"
            aria-label="AI filing triage"
          >
            <header class="ai-triage-heading">
              <div>
                <span class="ai-label">AI Triage</span>
                <strong>{{ categoryLabel(updateEvent.aiTriage.filingCategory) }}</strong>
              </div>
              <span>{{ confidenceLabel(updateEvent.aiTriage.confidence) }} confidence</span>
            </header>

            <div class="ai-section">
              <span>Filing summary</span>
              <p>{{ updateEvent.aiTriage.summary }}</p>
            </div>

            <div class="ai-section">
              <span>Attorney rationale</span>
              <p>{{ updateEvent.aiTriage.attorneyRationale }}</p>
            </div>

            <div class="client-draft">
              <div class="draft-heading">
                <span>Possible client update</span>
                <em>AI draft · Attorney approval required</em>
              </div>
              <strong>{{ updateEvent.aiTriage.clientTitle }}</strong>
              <p>{{ updateEvent.aiTriage.clientUpdate }}</p>
            </div>

            <div
              v-if="updateEvent.aiTriage.requiresDocumentReview || updateEvent.aiTriage.riskFlags?.length"
              class="ai-flags"
            >
              <span v-if="updateEvent.aiTriage.requiresDocumentReview" class="ai-flag review-required">
                Review document
              </span>
              <span
                v-for="riskFlag in updateEvent.aiTriage.riskFlags"
                :key="riskFlag"
                class="ai-flag"
              >
                {{ categoryLabel(riskFlag) }}
              </span>
            </div>
          </section>

          <p
            v-else-if="updateEvent.aiTriage?.status === 'failed'"
            class="ai-state ai-failed"
          >
            AI triage was unavailable. The original filing remains ready for attorney review.
          </p>

          <p v-else class="ai-state">
            AI triage is running in the background. Refresh shortly to see the result.
          </p>

          <div v-if="availableDocuments(updateEvent).length" class="document-list">
            <a
              v-for="document in availableDocuments(updateEvent)"
              :key="document.courtListenerDocumentId"
              :href="documentLink(document)"
              class="document-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span class="material-icons-outlined" aria-hidden="true">description</span>
              <span>
                <strong>{{ documentLabel(document) }}</strong>
                <small v-if="document.pageCount">
                  {{ document.pageCount }} {{ document.pageCount === 1 ? "page" : "pages" }}
                </small>
              </span>
              <span class="material-icons-outlined open-icon" aria-hidden="true">open_in_new</span>
            </a>
          </div>

          <p v-else class="document-unavailable">
            No downloadable RECAP document is available for this entry.
          </p>
        </article>
      </div>
    </section>

    <section
      v-else
      id="monitored-mdls-panel"
      class="monitor-list"
      role="tabpanel"
      aria-labelledby="monitored-mdls-tab"
    >
      <div class="list-heading">
        <div>
          <h3 id="monitor-list-title">Monitored MDLs</h3>
          <p>{{ monitors.length }} {{ monitors.length === 1 ? "monitor" : "monitors" }}</p>
        </div>

        <button
          type="button"
          class="refresh-button"
          :disabled="loading"
          @click="loadDashboard"
        >
          {{ loading ? "Loading..." : "Refresh" }}
        </button>
      </div>

      <div v-if="loading" class="empty-state">Loading CourtListener monitors...</div>

      <div v-else-if="loadError" class="empty-state error-state">
        {{ loadError }}
      </div>

      <div v-else-if="monitors.length === 0" class="empty-state">
        <span class="material-icons-outlined" aria-hidden="true">notifications_none</span>
        <strong>No MDLs are being monitored yet.</strong>
        <span>Add the first monitor to connect an LLF Litigation.</span>
      </div>

      <div v-else class="monitor-table-wrapper">
        <table class="monitor-table">
          <thead>
            <tr>
              <th>Litigation</th>
              <th>MDL</th>
              <th>Court / Docket</th>
              <th>CourtListener ID</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="monitor in monitors" :key="monitor.id">
              <td>
                <strong>{{ monitor.caseName }}</strong>
                <span>{{ linkedLitigationName(monitor.litigationId) }}</span>
              </td>
              <td>MDL {{ monitor.mdlNumber }}</td>
              <td>
                <strong>{{ monitor.courtId.toUpperCase() }}</strong>
                <span>{{ monitor.docketNumber }}</span>
              </td>
              <td>{{ monitor.courtListenerDocketId }}</td>
              <td>
                <span class="status-pill" :class="{ disabled: !monitor.enabled }">
                  {{ monitor.enabled ? "Monitoring" : "Disabled" }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </section>
</template>

<script>
import { computed, onMounted, reactive, ref } from "vue";

import {
  fetchCourtListenerMonitors,
  fetchCourtListenerUpdateEvents,
  fetchLitigations,
  saveCourtListenerMonitor
} from "@/firebase/firestore";

const emptyForm = () => ({
  litigationId: "",
  mdlNumber: "",
  courtListenerDocketId: "",
  courtListenerAlertId: "",
  courtId: "",
  docketNumber: "",
  caseName: "",
  enabled: true
});

export default {
  name: "UpdateDashboard",

  props: {
    adminId: { type: String, default: "" }
  },

  setup(props) {
    const litigations = ref([]);
    const monitors = ref([]);
    const updateEvents = ref([]);
    const activeTab = ref("incoming");
    const loading = ref(false);
    const saving = ref(false);
    const showAddMonitor = ref(false);
    const loadError = ref("");
    const formError = ref("");
    const successMessage = ref("");
    const form = reactive(emptyForm());

    const monitoredLitigationIds = computed(() => new Set(
      monitors.value.map(monitor => monitor.litigationId)
    ));

    const availableLitigations = computed(() => litigations.value.filter(
      litigation => !monitoredLitigationIds.value.has(litigation.litigationId)
    ));

    const pendingReviewCount = computed(() => updateEvents.value.filter(
      updateEvent => updateEvent.status === "needsReview"
    ).length);

    const canSave = computed(() => Boolean(
      props.adminId &&
      form.litigationId &&
      Number.isSafeInteger(Number(form.mdlNumber)) &&
      Number(form.mdlNumber) > 0 &&
      Number.isSafeInteger(Number(form.courtListenerDocketId)) &&
      Number(form.courtListenerDocketId) > 0 &&
      form.courtId.trim() &&
      form.docketNumber.trim() &&
      form.caseName.trim()
    ));

    const litigationLabel = litigation => {
      const name = litigation.publicActionName || litigation.internalName;
      const internalName = litigation.internalName;

      return internalName && internalName !== name
        ? `${name} — ${internalName}`
        : name;
    };

    const linkedLitigationName = litigationId => {
      const litigation = litigations.value.find(
        item => item.litigationId === litigationId
      );

      return litigation ? litigationLabel(litigation) : litigationId;
    };

    const formatDate = value => {
      const date = typeof value?.toDate === "function"
        ? value.toDate()
        : new Date(value);

      if (Number.isNaN(date.getTime())) { return "Date unavailable"; }

      return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      }).format(date);
    };

    const documentLink = document => {
      if (String(document?.filepathIa ?? "").startsWith("https://archive.org/")) {
        return document.filepathIa;
      }

      const absoluteUrl = String(document?.absoluteUrl ?? "");

      return absoluteUrl.startsWith("/docket/")
        ? `https://www.courtlistener.com${absoluteUrl}`
        : "";
    };

    const availableDocuments = updateEvent => (
      Array.isArray(updateEvent?.documents) ? updateEvent.documents : []
    ).filter(document => document.isAvailable === true && documentLink(document));

    const documentLabel = document => {
      if (document.description) { return document.description; }

      const attachment = document.attachmentNumber
        ? `, Attachment ${document.attachmentNumber}`
        : "";

      return `Document ${document.documentNumber || "available"}${attachment}`;
    };

    const categoryLabel = value => String(value ?? "Other")
      .split("_")
      .filter(Boolean)
      .map(word => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
      .join(" ");

    const confidenceLabel = value => {
      const confidence = Number(value);

      return Number.isFinite(confidence)
        ? `${Math.round(confidence * 100)}%`
        : "Unknown";
    };

    const triageLabel = updateEvent => {
      const triage = updateEvent?.aiTriage;

      if (triage?.status === "completed") {
        return `${categoryLabel(triage.priority)} Priority`;
      }

      if (triage?.status === "failed") { return "AI Unavailable"; }
      if (!triage) { return "Not Analyzed"; }

      return "AI Reviewing";
    };

    const triagePriorityClass = updateEvent => {
      const triage = updateEvent?.aiTriage;

      return triage?.status === "completed"
        ? `priority-${triage.priority}`
        : `triage-${triage?.status || "unavailable"}`;
    };

    const resetForm = () => {
      Object.assign(form, emptyForm());
      formError.value = "";
    };

    const openAddMonitor = () => {
      successMessage.value = "";
      resetForm();
      showAddMonitor.value = true;
    };

    const closeAddMonitor = () => {
      if (saving.value) { return; }

      resetForm();
      showAddMonitor.value = false;
    };

    const applyLitigationName = () => {
      const litigation = litigations.value.find(
        item => item.litigationId === form.litigationId
      );

      form.caseName = litigation
        ? litigation.publicActionName || litigation.internalName || ""
        : "";
    };

    const loadDashboard = async () => {
      loading.value = true;
      loadError.value = "";

      try {
        const [litigationRecords, monitorRecords, eventRecords] = await Promise.all([
          fetchLitigations(),
          fetchCourtListenerMonitors(),
          fetchCourtListenerUpdateEvents()
        ]);

        litigations.value = litigationRecords;
        monitors.value = monitorRecords;
        updateEvents.value = eventRecords;
      } catch (error) {
        console.error("Unable to load CourtListener monitoring:", error);
        loadError.value = "Unable to load CourtListener monitoring records.";
      } finally {
        loading.value = false;
      }
    };

    const saveMonitor = async () => {
      if (!canSave.value || saving.value) { return; }

      saving.value = true;
      formError.value = "";
      successMessage.value = "";

      try {
        await saveCourtListenerMonitor({ ...form }, props.adminId);
        await loadDashboard();
        showAddMonitor.value = false;
        resetForm();
        successMessage.value = "The MDL is now linked and ready to receive CourtListener events.";
      } catch (error) {
        console.error("Unable to save CourtListener monitor:", error);
        formError.value = error?.message || "Unable to save this monitor.";
      } finally {
        saving.value = false;
      }
    };

    onMounted(loadDashboard);

    return {
      activeTab,
      availableLitigations,
      applyLitigationName,
      availableDocuments,
      canSave,
      categoryLabel,
      closeAddMonitor,
      confidenceLabel,
      documentLabel,
      documentLink,
      form,
      formError,
      formatDate,
      litigationLabel,
      litigations,
      linkedLitigationName,
      loadDashboard,
      loadError,
      loading,
      monitors,
      openAddMonitor,
      pendingReviewCount,
      saveMonitor,
      saving,
      showAddMonitor,
      successMessage,
      triageLabel,
      triagePriorityClass,
      updateEvents
    };
  }
};
</script>

<style scoped>
.update-dashboard {
  display: flex;
  min-height: 100%;
  flex-direction: column;
  gap: 22px;
  padding: 24px;
  box-sizing: border-box;
  overflow-y: auto;
  color: #27352f;
}

.dashboard-header,
.form-heading,
.list-heading,
.form-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.dashboard-header h2,
.monitor-form h3,
.monitor-list h3,
.review-queue h3 {
  margin: 0;
  color: #25322c;
}

.eyebrow {
  margin: 0 0 4px;
  color: #168557;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.form-heading p,
.list-heading p {
  margin: 5px 0 0;
  color: #69746f;
}

.save-button,
.cancel-button,
.refresh-button,
.close-button {
  border-radius: 9px;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.monitor-form,
.monitor-list,
.review-queue {
  border: 1px solid #e0e7e3;
  border-radius: 13px;
  background: #ffffff;
}

.updates-tabs {
  display: inline-flex;
  align-self: flex-start;
  gap: 5px;
  padding: 5px;
  border: 1px solid #dce4df;
  border-radius: 11px;
  background: #f3f6f4;
}

.updates-tabs button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 13px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #637069;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.updates-tabs button:hover { color: #2f3e36; }

.updates-tabs button.active {
  background: #ffffff;
  color: #176e4c;
  box-shadow: 0 1px 4px rgba(35, 58, 47, 0.12);
}

.updates-tabs button span {
  min-width: 20px;
  padding: 2px 6px;
  border-radius: 999px;
  background: #e2e8e4;
  color: #657169;
  box-sizing: border-box;
  font-size: 0.72rem;
  line-height: 1.35;
  text-align: center;
}

.updates-tabs button.active span { background: #e5f5ed; color: #15734d; }

.monitor-form { padding: 22px; }
.form-heading { margin-bottom: 22px; align-items: flex-start; }

.close-button {
  display: grid;
  width: 38px;
  height: 38px;
  place-items: center;
  padding: 0;
  border: 1px solid #dce3df;
  background: #ffffff;
  color: #68736e;
}

.field-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

.field-group {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 7px;
  color: #34423b;
  font-weight: 700;
}

.field-group em {
  color: #77817c;
  font-size: 0.8rem;
  font-style: normal;
  font-weight: 500;
}

.field-group input,
.field-group select {
  width: 100%;
  box-sizing: border-box;
  padding: 11px 12px;
  border: 1px solid #d5ddd8;
  border-radius: 9px;
  background: #ffffff;
  color: #27352f;
  font: inherit;
  font-weight: 500;
}

.field-group input:focus,
.field-group select:focus {
  border-color: #168557;
  outline: 3px solid rgba(22, 133, 87, 0.12);
}

.field-group small {
  color: #75807a;
  font-size: 0.78rem;
  font-weight: 500;
}

.field-group code { color: #52615a; }
.full-width { grid-column: 1 / -1; }

.form-actions {
  justify-content: flex-end;
  margin-top: 22px;
}

.save-button,
.cancel-button,
.refresh-button { padding: 10px 16px; }
.save-button { border: 0; background: #18ab69; color: #ffffff; }
.cancel-button { border: 1px solid #d5ddd8; background: #ffffff; color: #46544d; }
.refresh-button { border: 1px solid #d5ddd8; background: #ffffff; color: #46544d; }
button:disabled { cursor: not-allowed; opacity: 0.55; }

.message {
  margin: 0;
  padding: 12px 14px;
  border-radius: 9px;
}

.error-message,
.error-state { border: 1px solid #efc5c5; background: #fff7f7; color: #a33636; }
.success-message { border: 1px solid #bde3cf; background: #f0fbf5; color: #18734d; }

.list-heading {
  padding: 19px 21px;
  border-bottom: 1px solid #e5ebe7;
}

.list-heading p { font-size: 0.86rem; }

.empty-state {
  display: flex;
  min-height: 180px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 7px;
  padding: 24px;
  color: #727d77;
  text-align: center;
}

.empty-state .material-icons-outlined { color: #9ca8a2; font-size: 34px; }
.empty-state strong { color: #445149; }
.monitor-table-wrapper { overflow-x: auto; }
.monitor-table { width: 100%; border-collapse: collapse; }

.monitor-table th,
.monitor-table td {
  padding: 14px 18px;
  border-bottom: 1px solid #edf1ef;
  text-align: left;
  vertical-align: middle;
}

.monitor-table th {
  background: #f8faf9;
  color: #66716b;
  font-size: 0.76rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.monitor-table td { color: #334139; }
.monitor-table tbody tr:last-child td { border-bottom: 0; }
.monitor-table td strong,
.monitor-table td span { display: block; }
.monitor-table td > span:not(.status-pill) { margin-top: 3px; color: #76807b; font-size: 0.82rem; }

.status-pill {
  display: inline-block !important;
  padding: 5px 9px;
  border-radius: 999px;
  background: #e8f7ef;
  color: #168557;
  font-size: 0.78rem;
  font-weight: 800;
}

.status-pill.disabled { background: #f0f1f0; color: #727a76; }

.private-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #6c7771;
  font-size: 0.8rem;
  font-weight: 700;
}

.private-label .material-icons-outlined { font-size: 17px; }
.queue-actions,
.event-badges {
  display: flex;
  align-items: center;
  gap: 10px;
}

.event-list { display: flex; flex-direction: column; }

.event-card {
  padding: 20px 21px;
  border-bottom: 1px solid #e9eeeb;
}

.event-card:last-child { border-bottom: 0; }

.event-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
}

.event-header h4 { margin: 5px 0 0; color: #2d3b34; font-size: 1.04rem; }
.event-header p { margin: 3px 0 0; color: #748079; font-size: 0.84rem; }
.event-kicker { display: flex; flex-wrap: wrap; gap: 7px; }

.event-kicker span {
  color: #68746d;
  font-size: 0.76rem;
  font-weight: 700;
}

.event-kicker span + span::before { margin-right: 7px; content: "·"; }

.review-pill {
  flex: 0 0 auto;
  padding: 5px 9px;
  border-radius: 999px;
  background: #fff4da;
  color: #8b6412;
  font-size: 0.76rem;
  font-weight: 800;
}

.triage-pill {
  flex: 0 0 auto;
  padding: 5px 9px;
  border-radius: 999px;
  background: #eef1ef;
  color: #66716b;
  font-size: 0.76rem;
  font-weight: 800;
}

.triage-pill.priority-green { background: #e7f7ef; color: #13784c; }
.triage-pill.priority-yellow { background: #fff4d7; color: #89600d; }
.triage-pill.priority-red { background: #f6e9e7; color: #9b3e35; }
.triage-pill.triage-failed { background: #f4e9e9; color: #9b4343; }

.event-description {
  margin: 17px 0 0;
  color: #3d4943;
  line-height: 1.55;
  white-space: pre-wrap;
}

.ai-triage-card {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: 17px;
  padding: 16px;
  border: 1px solid #dce5e0;
  border-radius: 10px;
  background: #f8faf9;
}

.ai-triage-heading,
.draft-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.ai-triage-heading > div { display: flex; align-items: center; gap: 9px; }
.ai-triage-heading > span { color: #737e78; font-size: 0.78rem; font-weight: 700; }
.ai-label { color: #176b4a; font-size: 0.72rem; font-weight: 900; letter-spacing: 0.08em; text-transform: uppercase; }
.ai-triage-heading strong { color: #34423b; }

.ai-section > span,
.draft-heading > span {
  display: block;
  margin-bottom: 4px;
  color: #6b7770;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.ai-section p,
.client-draft p { margin: 0; color: #3d4943; line-height: 1.5; }

.client-draft {
  padding: 14px;
  border-left: 4px solid #77b99a;
  border-radius: 7px;
  background: #ffffff;
}

.draft-heading em { color: #7a847f; font-size: 0.75rem; font-style: normal; }
.client-draft > strong { display: block; margin-bottom: 5px; color: #2d3b34; }
.ai-flags { display: flex; flex-wrap: wrap; gap: 7px; }

.ai-flag {
  padding: 4px 8px;
  border-radius: 999px;
  background: #edf0ee;
  color: #59655f;
  font-size: 0.72rem;
  font-weight: 700;
}

.ai-flag.review-required { background: #fff1d2; color: #805b0f; }

.ai-state {
  margin: 15px 0 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f1f4f2;
  color: #68736d;
  font-size: 0.84rem;
}

.ai-state.ai-failed { background: #fff5f4; color: #92453d; }

.document-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 17px;
}

.document-link {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 10px;
  padding: 11px 12px;
  border: 1px solid #dce5e0;
  border-radius: 9px;
  background: #f9fbfa;
  color: #245f47;
  text-decoration: none;
}

.document-link:hover { border-color: #8bc4a9; background: #f1faf5; }
.document-link > span:nth-child(2) { min-width: 0; flex: 1; }
.document-link strong { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.document-link small { display: block; margin-top: 2px; color: #748079; }
.document-link .open-icon { color: #748079; font-size: 18px; }
.document-unavailable { margin: 15px 0 0; color: #818a85; font-size: 0.84rem; }

@media (max-width: 780px) {
  .update-dashboard { padding: 18px; }
  .updates-tabs { width: 100%; box-sizing: border-box; }
  .updates-tabs button { flex: 1; justify-content: center; }
  .dashboard-header { align-items: flex-start; }
  .field-grid { grid-template-columns: 1fr; }
  .document-list { grid-template-columns: 1fr; }
  .full-width { grid-column: auto; }
  .list-heading,
  .event-header,
  .ai-triage-heading,
  .draft-heading { align-items: flex-start; flex-direction: column; }
  .queue-actions { width: 100%; justify-content: space-between; }
}
</style>
